import { CorrelationCalculationType, CorrelationResultType, korrelationFilter, zwischenWertObject } from "./korrelationTypes";
import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import type { RootState, AppDispatch } from "../../../app/store";
import { getWithAuth } from "../../../utils/api";
import { logError } from "../../../utils/utils";
import { analyseAnfrage } from "../../../utils/apiTypes";
import { errorHandler } from "../../../utils/errorHandling";

const zwischenWert : zwischenWertObject = {
    value: "Alle",
    groupBy: "",
    id: ""
}

const initialState: korrelationFilter 
  = {
    calculatedKorrelationenList: [],
    loadingCalculatedKorrelationen: false,
    selectedCalculatedKorrelation: {} as analyseAnfrage,

    korrelationZwischen: CorrelationCalculationType.none,
    korrelationZwischenTemp: CorrelationCalculationType.none,
    korrelationZwischenVorher: CorrelationCalculationType.none,

    korrelationZwischenWert: zwischenWert,
    korrelationZwischenWertTemp: zwischenWert,
    korrelationZwischenWertVorher: zwischenWert,

    slider: [-1.0,1.0],
    sliderTemp: [-1.0,1.0],
    sliderVorher: [-1.0,1.0],

    ergebnistyp: CorrelationResultType.single,

    openGroups: [],

    isListeLoading: false,
    isChanged: false,
    isInitFirstLoad: true,
}

const korrelationFilterSlice = createSlice({
  name: "korrelationFilter",
  initialState,
  reducers: 
  {
    setCalculatedKorrelationenList: (state, action: PayloadAction<Array<analyseAnfrage>>) => {
      state.calculatedKorrelationenList = action.payload;
    },
    setLoadingCalculatedKorrelationen: (state, action: PayloadAction<boolean>) => {
      state.loadingCalculatedKorrelationen = action.payload;
    },
    setSelectedCalculatedKorrelation: (state, action: PayloadAction<analyseAnfrage>) => {
      state.selectedCalculatedKorrelation = action.payload;
      state.ergebnistyp = action.payload.korrelationsergebnistyp ?? CorrelationResultType.single;
    },
    setKorrelationZwischen : (state) => {
        state.korrelationZwischenVorher = state.korrelationZwischenTemp;
        state.korrelationZwischen = state.korrelationZwischenTemp;
    },
    setKorrelationZwischenTemp : (state, action:PayloadAction<CorrelationCalculationType>) => {
        state.korrelationZwischenTemp = action.payload;
    },
    resetKorrelationZwischen : (state) => {
        state.korrelationZwischenTemp = state.korrelationZwischenVorher;
        state.korrelationZwischen = state.korrelationZwischenVorher;
    },
    setKorrelationZwischenWert : (state) => {
        state.korrelationZwischenWertVorher = state.korrelationZwischenWertTemp;
        state.korrelationZwischenWert = state.korrelationZwischenWertTemp;
    },
    setKorrelationZwischenWertTemp : (state, action:PayloadAction<zwischenWertObject>) => {
        state.korrelationZwischenWertTemp = action.payload;
    },
    resetKorrelationZwischenWert : (state) => {
        state.korrelationZwischenWertTemp = state.korrelationZwischenWertVorher;
        state.korrelationZwischenWert = state.korrelationZwischenWertVorher;
    },
    setSlider: (state) => {
        state.sliderVorher = state.sliderTemp;
        state.slider = state.sliderTemp;
    },
    setSliderTemp: (state, action: PayloadAction< number[]>) => {
        state.sliderTemp = action.payload;
    },
    resetSlider: (state) => {
        state.sliderTemp = state.sliderVorher;
        state.slider = state.sliderVorher;
    },
    setErgebnistyp : (state, action: PayloadAction<CorrelationResultType>) => {
        state.ergebnistyp = action.payload;
    },
    setOpenGroups:(state, action: PayloadAction<string[]>) => {
        state.openGroups = action.payload;
    },
    setIsListeLoading : (state, action:PayloadAction<boolean>) => {
        state.isListeLoading = action.payload;
    },
    setIsChangedKorrelation : (state, action:PayloadAction<boolean>) => {
        state.isChanged = action.payload;
    },
    setInitFirstLoad: (state, action:PayloadAction<boolean>) => {
        state.isInitFirstLoad = action.payload;
    }
  }
});

export const selectCalculatedKorrelationenList = (state: RootState): Array<analyseAnfrage> => state.korrelationFilter.calculatedKorrelationenList;
export const selectLoadingCalculatedKorrelationen = (state: RootState): boolean => state.korrelationFilter.loadingCalculatedKorrelationen;
export const selectselectedCalculatedKorrelation = (state: RootState): analyseAnfrage => state.korrelationFilter.selectedCalculatedKorrelation;
export const selectKorrelationZwischen = (state:RootState) : CorrelationCalculationType => state.korrelationFilter.korrelationZwischen;
export const selectKorrelationZwischenTemp = (state:RootState) : CorrelationCalculationType => state.korrelationFilter.korrelationZwischenTemp;
export const selectKorrelationZwischenWert = (state:RootState) : zwischenWertObject => state.korrelationFilter.korrelationZwischenWert;
export const selectKorrelationZwischenWertTemp = (state:RootState) : zwischenWertObject => state.korrelationFilter.korrelationZwischenWertTemp;
export const selectKenngroesseListe = (state:RootState) : zwischenWertObject[] => [{value:"Alle", groupBy:"", id:""}].concat(state.kenngroesseFilter.kenngroesseListe.map(x => ({value: x.beschreibung + " (" + x.feldname + ")", groupBy: x.unterkategorie ?? "", id: x.feldname})));
export const selectMessstelleListe = (state:RootState) : zwischenWertObject[] => [{value:"Alle", groupBy:"", id:""}].concat(state.messstellenFilter.messstellenListe.map(x => ({value: x.schaltfeld + " (" + x.name + ")", groupBy: x.umspannwerk ?? "", id: x.name})));
export const selectSlider = (state:RootState) : number[] => state.korrelationFilter.slider;
export const selectSliderTemp = (state:RootState) : number[] => state.korrelationFilter.sliderTemp;
export const selectErgebnistyp = (state: RootState): CorrelationResultType => state.korrelationFilter.ergebnistyp;
export const selectopenGroups = (state: RootState): string[] => state.korrelationFilter.openGroups;
export const selectIsListeLoading = (state:RootState) : boolean => state.korrelationFilter.isListeLoading;
export const selectIsChangedKorrelation = (state:RootState) : boolean => state.korrelationFilter.isChanged;
export const selectIsInitFirstLoad = (state:RootState) : boolean => state.korrelationFilter.isInitFirstLoad;

export const {
    setCalculatedKorrelationenList,
    setLoadingCalculatedKorrelationen,
    setSelectedCalculatedKorrelation,
    setKorrelationZwischen,
    setKorrelationZwischenTemp,
    setKorrelationZwischenWert,
    setKorrelationZwischenWertTemp,
    setSlider,
    setSliderTemp,
    setOpenGroups,
    resetKorrelationZwischenWert,
    resetKorrelationZwischen,
    resetSlider,
    setErgebnistyp,
    setIsListeLoading,
    setIsChangedKorrelation,
    setInitFirstLoad
} = korrelationFilterSlice.actions;


export const getCalculatedKorrelationen = (idString: string | undefined) => (dispatch: AppDispatch): void => {
  dispatch(setLoadingCalculatedKorrelationen(true));
  getWithAuth("KORRELATIONLIST", {})
    .then((response: Array<analyseAnfrage>) => {
      if (idString) {
          const id = Number(idString);
          const entry = response.find(x => x.id == id);
          if (entry) dispatch(setSelectedCalculatedKorrelation(entry));
      }
      dispatch(setCalculatedKorrelationenList(response));
    })
    .catch((error) => {
      logError(error);
      errorHandler(error, dispatch);
    }).finally(() => dispatch(setLoadingCalculatedKorrelationen(false)));
}

export default korrelationFilterSlice.reducer;